
import { useEffect, useState, useRef } from 'react';

export const useIntersectionObserver = (
  options = { threshold: 0.1, rootMargin: '0px' }
) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (ref.current) observer.unobserve(ref.current);
      }
    }, options);

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options]);

  return { ref, isVisible };
};

export const useParallax = (speed = 0.1) => {
  const ref = useRef<HTMLElement | null>(null);
  
  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      
      const scrollY = window.scrollY;
      const elementY = scrollY * speed;
      
      ref.current.style.transform = `translateY(${elementY}px)`;
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [speed]);
  
  return ref;
};

export const useTextReveal = (duration = 0.5, delay = 0.1) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isRevealed) {
          setIsRevealed(true);
          
          const element = ref.current;
          if (!element) return;
          
          const textContent = element.textContent || '';
          element.textContent = '';
          element.style.visibility = 'visible';
          
          [...textContent].forEach((char, index) => {
            const span = document.createElement('span');
            span.textContent = char;
            span.style.opacity = '0';
            span.style.display = 'inline-block';
            span.style.transition = `opacity ${duration}s ease`;
            span.style.transitionDelay = `${index * delay}s`;
            
            element.appendChild(span);
            
            setTimeout(() => {
              span.style.opacity = '1';
            }, 10);
          });
        }
      },
      { threshold: 0.5 }
    );
    
    observer.observe(ref.current);
    
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [duration, delay, isRevealed]);
  
  return { ref, isRevealed };
};

export const useFixedOnScroll = (startOffset: number, endOffset: number) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [hasExited, setHasExited] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      
      const scrollY = window.scrollY;
      
      if (scrollY >= startOffset && scrollY <= endOffset) {
        setIsFixed(true);
        setHasExited(false);
      } else if (scrollY > endOffset) {
        setIsFixed(false);
        setHasExited(true);
      } else {
        setIsFixed(false);
        setHasExited(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [startOffset, endOffset]);
  
  return { ref, isFixed, hasExited };
};

export const getRandomFloat = (min: number, max: number) => {
  return Math.random() * (max - min) + min;
};
